import { Injectable } from "@angular/core";
import { VideoEvent } from "../models/video-event.model";
import { VideoEventType } from "../models/video-event-type.enum";
import { BufferItem, VideoBufferingInfo } from "../models/video-buffering-info.model";
import { DateTimeUtilService } from "../../shared/util/date-time-util.service";
import { DwLoggingService } from "@devwareapps/devware-cap";
import { VideoEntity } from "../../../meta-data/app-meta-data.service";


@Injectable({
    providedIn: 'root'
})
export class VideoBufferingUtilService {
    bufferingThresholdSeconds: number = 2;
    startupBufferingThresholdSeconds: number = 8;
    maxReliableProgressGapSeconds: number = 8;
    minReliableProgressGapSeconds: number = 1;

    constructor(private dateTimeUtilService: DateTimeUtilService,
        private dwLoggingService: DwLoggingService
    ) {
    }

    loggingEnabled: boolean = true;

    videoBuffingInfo: VideoBufferingInfo = {
        bufferingItems: []
    };

    currentBufferInfo: BufferItem;
    private currentVideoId: number;

    private initBufferInfo() {
        this.videoBuffingInfo = {
            bufferingItems: []
        };
        this.currentBufferInfo = null;
    }

    public handleBufferingEvent(video: VideoEntity, videoEvent: VideoEvent) {
        if (!this.loggingEnabled || !video || !videoEvent) {
            return;
        }

        this.resetIfVideoChanged(video);

        switch (videoEvent.videoEventType) {
            case VideoEventType.videoStarted:
                this.initBufferInfo();
                this.currentVideoId = video.VideoId;
                this.videoBuffingInfo.startUpbufferTime = {
                    lastUpdateDateTime: new Date().toISOString(),
                    lastUpdateTimeSeconds: 0
                }
                break;
            case VideoEventType.videoPaused:
            case VideoEventType.videoStopped:

                if (this.videoBuffingInfo?.bufferingItems?.length > 0) {
                    this.writeLogMessage(video, ` ${this.videoBuffingInfo.bufferingItems.length} Buffering Issues`, this.videoBuffingInfo.bufferingItems.reduce((a, b) => a + b.bufferingTimeSeconds, 0), {
                        bufferingItems: this.videoBuffingInfo.bufferingItems.map(item => this.buildBufferingItemDiagnostic(item)),
                        bufferingThresholdSeconds: this.bufferingThresholdSeconds,
                        startupBufferingThresholdSeconds: this.startupBufferingThresholdSeconds,
                        maxReliableProgressGapSeconds: this.maxReliableProgressGapSeconds,
                        minReliableProgressGapSeconds: this.minReliableProgressGapSeconds
                    });
                }

                this.initBufferInfo();
                break;
            case VideoEventType.videoProgress:

                if (!this.videoBuffingInfo || this.shouldIgnoreProgressEvent(videoEvent)) {
                    this.resetBufferBaseline(videoEvent);
                    break;
                }

                // Handle Startup Buffering Time
                if (this.videoBuffingInfo.startUpbufferTime?.lastUpdateDateTime && !this.videoBuffingInfo.startUpbufferTime.currentUpdateDateTime) {
                    this.handleStartupBufferingTime(video, videoEvent);
                    break;
                }

                if (this.currentBufferInfo) {
                    this.currentBufferInfo.currentUpdateDateTime = new Date().toISOString();
                    this.currentBufferInfo.currentUpdateTimeSeconds = videoEvent.videoTime;


                    const actualSecondsPassed = this.dateTimeUtilService.calcDurationSeconds(this.currentBufferInfo.lastUpdateDateTime, this.currentBufferInfo.currentUpdateDateTime);

                    const playerSecondsPassed = this.currentBufferInfo.currentUpdateTimeSeconds - this.currentBufferInfo.lastUpdateTimeSeconds;

                    if (!this.isReliableBufferSample(actualSecondsPassed, playerSecondsPassed)) {
                        this.resetBufferBaseline(videoEvent);
                        break;
                    }

                    this.currentBufferInfo.bufferingTimeSeconds = actualSecondsPassed - playerSecondsPassed;

                    if (this.currentBufferInfo.bufferingTimeSeconds >= this.bufferingThresholdSeconds) {
                        this.videoBuffingInfo.bufferingItems.push(this.currentBufferInfo);

                      //  this.writeLogMessage(video, 'Buffering Time', this.currentBufferInfo.bufferingTimeSeconds);

                        this.currentBufferInfo = null;
                    }
                }

                if (!this.currentBufferInfo) {
                    this.currentBufferInfo = {
                        lastUpdateDateTime: new Date().toISOString(),
                        lastUpdateTimeSeconds: videoEvent.videoTime
                    }
                } else {
                    this.currentBufferInfo.currentUpdateDateTime = new Date().toISOString();
                    this.currentBufferInfo.currentUpdateTimeSeconds = videoEvent.videoTime;
                }


                break;

        }
    }

    private resetIfVideoChanged(video: VideoEntity) {
        if (!this.currentVideoId || this.currentVideoId === video.VideoId) {
            return;
        }

        this.initBufferInfo();
        this.currentVideoId = video.VideoId;
    }

    private shouldIgnoreProgressEvent(videoEvent: VideoEvent): boolean {
        return document.visibilityState !== 'visible'
            || videoEvent.userSeeked
            || !!videoEvent.eventData?.isFallbackProgress
            || typeof videoEvent.videoTime !== 'number'
            || isNaN(videoEvent.videoTime);
    }

    private isReliableBufferSample(actualSecondsPassed: number, playerSecondsPassed: number): boolean {
        if (actualSecondsPassed < this.minReliableProgressGapSeconds || actualSecondsPassed > this.maxReliableProgressGapSeconds) {
            return false;
        }

        if (playerSecondsPassed < 0) {
            return false;
        }

        return playerSecondsPassed <= actualSecondsPassed + 1;
    }

    private resetBufferBaseline(videoEvent: VideoEvent) {
        if (typeof videoEvent.videoTime !== 'number' || isNaN(videoEvent.videoTime)) {
            this.currentBufferInfo = null;
            return;
        }

        this.currentBufferInfo = {
            lastUpdateDateTime: new Date().toISOString(),
            lastUpdateTimeSeconds: videoEvent.videoTime
        };
    }

    private handleStartupBufferingTime(video: VideoEntity, videoEvent: VideoEvent) {
        if (this.shouldIgnoreProgressEvent(videoEvent)) {
            this.videoBuffingInfo.startUpbufferTime = null;
            this.resetBufferBaseline(videoEvent);
            return;
        }

        // Handle Startup Buffering Time
        this.videoBuffingInfo.startUpbufferTime.currentUpdateDateTime = new Date().toISOString();
        this.videoBuffingInfo.startUpbufferTime.currentUpdateTimeSeconds = videoEvent.videoTime;

        this.videoBuffingInfo.startUpbufferTime.bufferingTimeSeconds = this.dateTimeUtilService.calcDurationSeconds(this.videoBuffingInfo.startUpbufferTime.lastUpdateDateTime, this.videoBuffingInfo.startUpbufferTime.currentUpdateDateTime);

        // this.dwLoggingService.logToConsole('Buffering Startup Time: ' + this.videoBuffingInfo.startUpbufferTime.bufferingTimeSeconds);

        if (this.videoBuffingInfo.startUpbufferTime.bufferingTimeSeconds >= this.startupBufferingThresholdSeconds) {
            this.writeLogMessage(video, 'Startup Time', this.videoBuffingInfo.startUpbufferTime.bufferingTimeSeconds, {
                startupBufferingItem: this.buildBufferingItemDiagnostic(this.videoBuffingInfo.startUpbufferTime),
                startupBufferingThresholdSeconds: this.startupBufferingThresholdSeconds
            });
        }
    }

    writeLogMessage(video: VideoEntity, messageDetails: string, bufferingTimeSeconds: number, diagnosticData?: any) {
        const message = `Video Log: ${video.VideoName} (${video.VideoId}) - ${messageDetails} ${bufferingTimeSeconds.toFixed(1)} seconds`;

        this.dwLoggingService.logWarning(message, {
            videoId: video.VideoId,
            videoName: video.VideoName,
            totalBufferingTimeSeconds: Number(bufferingTimeSeconds.toFixed(1)),
            ...(diagnosticData || {})
        }, true);
    }

    private buildBufferingItemDiagnostic(item: BufferItem) {
        return {
            lastUpdateDateTime: item.lastUpdateDateTime,
            currentUpdateDateTime: item.currentUpdateDateTime,
            lastUpdateTimeSeconds: item.lastUpdateTimeSeconds,
            currentUpdateTimeSeconds: item.currentUpdateTimeSeconds,
            bufferingTimeSeconds: item.bufferingTimeSeconds
        };
    }

}
